import { useEffect, useState } from "react";
import { AlertTriangle, Activity, Power, Users as UsersIcon, TrendingUp, CheckCircle, ShieldAlert } from "lucide-react";
import AdminShell, { Panel, StatusPill, MockBadge, Btn } from "@/components/admin/AdminShell";
import KpiCard from "@/components/admin/KpiCard";
import { PrimaryTabs, FilterChips } from "@/components/admin/SubTabs";
import DataTable from "@/components/admin/DataTable";
import AdminPager from "@/components/admin/AdminPager";
import { api } from "@/lib/api";
import { asAdminList, useAdminPager, unwrapAdminList } from "@/lib/useAdminPager";

const TABS = [
  { id: "engine", label: "User Risk Engine", icon: AlertTriangle },
  { id: "alerts", label: "Risk Alerts", icon: ShieldAlert },
  { id: "margin", label: "Margin & Liquidations", icon: Activity },
  { id: "systemic", label: "Systemic Risk", icon: Activity },
  { id: "killswitch", label: "Kill Switches", icon: Power },
];

const STATUS_FILTERS = [{ id: "", label: "All" }, { id: "healthy", label: "Healthy" }, { id: "warning", label: "Warning" }, { id: "critical", label: "Critical" }];
const fmt = (v) => v?.toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }) || "—";
const fmtUsd = (v) => v?.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }) || "—";

export default function RiskManagement() {
  const [tab, setTab] = useState("engine");
  const [positions, setPositions] = useState([]);
  const [marginHealth, setMarginHealth] = useState([]);
  const [liquidationAlerts, setLiquidationAlerts] = useState([]);
  const [leverage, setLeverage] = useState([]);
  const [correlated, setCorrelated] = useState([]);
  const [riskAlerts, setRiskAlerts] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [killState, setKillState] = useState({});
  const [killBusy, setKillBusy] = useState("");
  const posPager = useAdminPager();
  const levPager = useAdminPager();
  const liqPager = useAdminPager();
  const corrPager = useAdminPager();
  const alertPager = useAdminPager();

  async function loadKillState() {
    try {
      const { data } = await api.get("/api/admin/safety/killswitch");
      setKillState(data || {});
    } catch (e) {
      console.error(e);
    }
  }

  async function setKill(key, value) {
    setKillBusy(key);
    try {
      const { data } = await api.post("/api/admin/safety/killswitch", { key, value });
      setKillState(data?.state || {});
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.detail || "Kill switch update failed");
    } finally {
      setKillBusy("");
    }
  }

  async function loadData() {
    try {
      const { data: posData } = await api.get("/api/admin/risk/positions", {
        params: { skip: posPager.skip, limit: posPager.limit },
      });
      const pos = unwrapAdminList(posData);
      setPositions(pos.items);
      posPager.setTotal(pos.total);
    } catch (e) {
      console.error(e);
    }
    try {
      const { data: liqData } = await api.get("/api/admin/safety/liquidation-alerts", {
        params: { skip: liqPager.skip, limit: liqPager.limit },
      });
      const liq = unwrapAdminList(liqData, "alerts");
      setLiquidationAlerts(liq.items);
      liqPager.setTotal(liq.total);
    } catch (e) {
      console.error(e);
    }
    try {
      const { data: levData } = await api.get("/api/admin/risk/leverage-abuse", {
        params: { skip: levPager.skip, limit: levPager.limit },
      });
      const lev = unwrapAdminList(levData);
      setLeverage(lev.items);
      levPager.setTotal(lev.total);
    } catch (e) {
      console.error(e);
    }
    try {
      const { data: corrData } = await api.get("/api/admin/risk/correlated", {
        params: { skip: corrPager.skip, limit: corrPager.limit },
      });
      const corr = unwrapAdminList(corrData);
      setCorrelated(corr.items);
      corrPager.setTotal(corr.total);
    } catch (e) {
      console.error(e);
    }
    try {
      const { data: riskData } = await api.get("/api/admin/safety/risk-alerts", {
        params: { status: "pending", skip: alertPager.skip, limit: alertPager.limit },
      });
      const alerts = unwrapAdminList(riskData, "alerts");
      setRiskAlerts(alerts.items);
      alertPager.setTotal(alerts.total);
    } catch (e) {
      console.error(e);
    }
  }

  async function resolveRiskAlert(alertId) {
    try {
      await api.post(`/api/admin/safety/risk-alerts/${alertId}/resolve`, { status: "resolved" });
      loadData();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to resolve alert");
    }
  }

  async function unfreezeUser(userId) {
    if (!userId) return;
    try {
      await api.post("/api/admin/safety/unfreeze-user", { user_id: userId });
      alert("User unfrozen");
      loadData();
    } catch (e) {
      alert(e.response?.data?.detail || "Unfreeze failed");
    }
  }

  useEffect(() => {
    loadData();
    const timer = setInterval(() => {
      loadData();
    }, 30000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [posPager.page, levPager.page, liqPager.page, corrPager.page, alertPager.page]);

  useEffect(() => {
    if (tab === "killswitch") loadKillState();
  }, [tab]);

  async function resolveLiquidation(alertId) {
    try {
      await api.post(`/api/admin/safety/liquidation-alerts/${alertId}/resolve`, { status: "resolved" });
      loadData();
    } catch (e) {
      alert("Failed to resolve liquidation alert");
    }
  }

  const positionRows = asAdminList(positions);
  const critical = positionRows.filter((p) => p.status === "critical").length;
  const warning = positionRows.filter((p) => p.status === "warning").length;
  const totalExposure = positionRows.reduce((s, p) => s + (p.total_position_inr || 0), 0);
  const avgMargin = positionRows.length ? (positionRows.reduce((s, p) => s + (p.margin_ratio_pct || 0), 0) / positionRows.length).toFixed(1) : 0;

  const filteredPositions = positionRows.filter((p) => !statusFilter || p.status === statusFilter);

  // Compute Risk Score Distribution Histogram (Buckets of 20)
  const buckets = [0, 0, 0, 0, 0]; // 0-20, 21-40, 41-60, 61-80, 81-100
  positionRows.forEach((p) => {
    const score = p.risk_score || 0;
    const idx = Math.min(4, Math.floor(score / 20));
    buckets[idx]++;
  });
  const maxBucketVal = Math.max(...buckets, 1);

  return (
    <AdminShell title="Risk Management">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        <KpiCard label="Users at Risk" value={positions.length} icon={UsersIcon} variant="yellow" />
        <KpiCard label="Total Exposure" value={fmt(totalExposure)} icon={TrendingUp} variant="blue" />
        <KpiCard label="Avg Margin Ratio" value={`${avgMargin}%`} icon={Activity} variant="purple" />
        <KpiCard label="Critical Alerts" value={critical} delta={`${warning} warning`} icon={AlertTriangle} variant="red" />
      </div>

      <PrimaryTabs tabs={TABS} value={tab} onChange={setTab} testIdPrefix="risk-tab" />
      <div className="mt-5">
        {tab === "engine" && (
          <div className="space-y-5">
            {/* Risk Score Distribution Histogram */}
            <Panel className="p-5">
              <h3 className="text-[14px] font-bold mb-4">Risk Score Distribution (Histogram)</h3>
              <div className="flex items-end justify-between h-32 gap-2 px-4 border-b border-[#1E2845] pb-2">
                {buckets.map((count, idx) => {
                  const pct = (count / maxBucketVal) * 100;
                  const label = `${idx * 20}–${(idx + 1) * 20}`;
                  const colors = ["bg-[#10B981]", "bg-[#3B82F6]", "bg-[#F59E0B]", "bg-[#EF4444]", "bg-[#B91C1C]"];
                  return (
                    <div key={idx} className="flex-1 flex flex-col items-center group relative">
                      <div className="text-[11px] text-[#8892A8] mb-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        {count} Users
                      </div>
                      <div 
                        className={`w-full rounded-t-[4px] ${colors[idx]} transition-all duration-500`}
                        style={{ height: `${Math.max(5, pct)}%` }}
                      />
                      <div className="text-[11px] text-[#8892A8] mt-2 font-mono">{label}</div>
                    </div>
                  );
                })}
              </div>
            </Panel>

            <Panel>
              <div className="p-4 border-b border-[#1E2845] flex justify-between items-center">
                <h3 className="text-[14px] font-bold">Position Exposure Monitor</h3>
                <FilterChips options={STATUS_FILTERS} value={statusFilter} onChange={setStatusFilter} testIdPrefix="risk-status" />
              </div>
              <DataTable
                testid="risk-positions-table"
                columns={[
                  { key: "user", header: "User", render: (p) => <div><div className="font-bold">{p.user_name}</div><div className="text-[11px] text-[#8892A8]">{p.user_email}</div></div> },
                  { key: "pos", header: "Position", align: "right", render: (p) => <span className="tabular-nums font-bold">{fmt(p.total_position_inr)}</span> },
                  { key: "lev", header: "Leverage", align: "right", render: (p) => <span className="tabular-nums">{p.leverage}x</span> },
                  { key: "mr", header: "Margin Ratio", align: "right", render: (p) => <span className={`tabular-nums font-bold ${p.margin_ratio_pct < 30 ? "text-[#EF4444]" : p.margin_ratio_pct < 70 ? "text-[#F59E0B]" : "text-[#10B981]"}`}>{p.margin_ratio_pct}%</span> },
                  { key: "score", header: "Risk Score", align: "right", render: (p) => <span className={`font-bold tabular-nums ${p.risk_score > 80 ? "text-[#EF4444]" : p.risk_score > 60 ? "text-[#F59E0B]" : "text-[#10B981]"}`}>{p.risk_score}</span> },
                  { key: "status", header: "Status", render: (p) => <StatusPill value={p.status} /> },
                  { key: "pnl", header: "24h PnL", align: "right", render: (p) => <span className={`font-semibold tabular-nums ${p.pnl_24h_inr >= 0 ? "text-[#10B981]" : "text-[#EF4444]"}`}>{fmt(p.pnl_24h_inr)}</span> },
                ]}
                rows={filteredPositions}
                rowKey={(p) => p.user_id}
              />
              <AdminPager page={posPager.page} total={posPager.total} pageSize={posPager.pageSize} onPage={posPager.setPage} testId="risk-pos-pager" />
            </Panel>
            <Panel>
              <DataTable
                columns={[
                  { key: "user", header: "User", render: (l) => <div><div className="font-bold">{l.user_name}</div><div className="text-[11px] text-[#8892A8]">{l.user_email}</div></div> },
                  { key: "type", header: "Type", render: (l) => <span className="font-semibold">{l.type}</span> },
                  { key: "details", header: "Details", render: (l) => <span className="text-[#8892A8] text-[12px]">{l.details}</span> },
                  { key: "risk", header: "Risk", render: (l) => <StatusPill value={l.risk?.toLowerCase()} /> },
                  { key: "status", header: "Status", render: (l) => <StatusPill value={l.status} /> },
                ]}
                rows={leverage}
                rowKey={(l) => l.alert_id}
              />
              <AdminPager page={levPager.page} total={levPager.total} pageSize={levPager.pageSize} onPage={levPager.setPage} testId="risk-lev-pager" />
            </Panel>
          </div>
        )}

        {tab === "alerts" && (
          <Panel>
            <div className="p-4 border-b border-[#1E2845] flex justify-between items-center">
              <h3 className="text-[14px] font-bold">Pending Risk Alerts</h3>
              <span className="text-[12px] text-[#8892A8]">{riskAlerts.length} open</span>
            </div>
            <DataTable
              testid="risk-alerts-table"
              columns={[
                { key: "type", header: "Type", render: (a) => a.type || "—" },
                { key: "user", header: "User", render: (a) => <span className="text-[12px]">{a.user_email}</span> },
                { key: "desc", header: "Description", render: (a) => <span className="text-[12px] text-[#8892A8] max-w-md truncate block">{a.description}</span> },
                { key: "level", header: "Level", render: (a) => <StatusPill value={a.risk_level} /> },
                { key: "score", header: "Score", align: "right", render: (a) => a.risk_score ?? "—" },
                {
                  key: "act",
                  header: "",
                  render: (a) => (
                    <div className="flex gap-2 justify-end">
                      <Btn kind="ghost" onClick={() => resolveRiskAlert(a.alert_id || a.id)}>Resolve</Btn>
                      {a.user_id && (
                        <Btn kind="success" onClick={() => unfreezeUser(a.user_id)}>Unfreeze</Btn>
                      )}
                    </div>
                  ),
                },
              ]}
              rows={riskAlerts}
              rowKey={(a) => a.alert_id || a.id}
            />
            <AdminPager page={alertPager.page} total={alertPager.total} pageSize={alertPager.pageSize} onPage={alertPager.setPage} testId="risk-alerts-pager" />
          </Panel>
        )}

        {tab === "margin" && (
          <div className="space-y-5">
            {/* Liquidation Queue Panel */}
            <Panel>
              <div className="p-4 border-b border-[#1E2845]"><h3 className="text-[14px] font-bold text-[#EF4444] flex items-center gap-2"><ShieldAlert size={16} /> Liquidation Queue (Admin Trigger Queue)</h3></div>
              <DataTable
                columns={[
                  { key: "user", header: "User", render: (a) => <div><div className="font-bold">{a.user_name}</div><div className="text-[11px] text-[#8892A8]">{a.user_email}</div></div> },
                  { key: "health", header: "Health Score", align: "center", render: (a) => <span className="font-mono text-[#EF4444] font-bold">{a.health_score} / 100</span> },
                  { key: "exp", header: "Total Exposure", align: "right", render: (a) => <span className="tabular-nums font-semibold">{fmtUsd(parseFloat(a.total_exposure_usdt))}</span> },
                  { key: "created", header: "Alert Time", render: (a) => <span className="text-[12px] text-[#8892A8]">{new Date(a.created_at).toLocaleString()}</span> },
                  { key: "status", header: "Status", render: (a) => <StatusPill value={a.status} /> },
                  { key: "actions", header: "Action", align: "right", render: (a) => (
                    a.status === "pending" ? (
                      <Btn kind="danger" onClick={() => resolveLiquidation(a.alert_id)} className="h-7 text-[11px]">
                        <CheckCircle size={12} /> Force Liquidate / Resolve
                      </Btn>
                    ) : (
                      <span className="text-[11px] text-[#8892A8]">Resolved</span>
                    )
                  )}
                ]}
                rows={liquidationAlerts}
                rowKey={(a) => a.alert_id}
                empty="No active pending liquidations."
              />
              <AdminPager page={liqPager.page} total={liqPager.total} pageSize={liqPager.pageSize} onPage={liqPager.setPage} testId="risk-liq-pager" />
            </Panel>

            {/* Margin Health Positions Monitor */}
            <Panel>
              <div className="p-4 border-b border-[#1E2845]"><h3 className="text-[14px] font-bold">Portfolio Concentration & Health Monitor</h3></div>
              <DataTable
                columns={[
                  { key: "user", header: "User", render: (p) => <div><div className="font-bold">{p.user_name}</div><div className="text-[11px] text-[#8892A8]">{p.user_email}</div></div> },
                  { key: "exp", header: "Total Exposure (USDT)", align: "right", render: (p) => <span className="font-semibold tabular-nums">{fmtUsd(p.total_position_inr / 85)}</span> },
                  { key: "conc", header: "Top Asset & Concentration", render: (p) => <div><span className="font-semibold text-[#E5E9F2]">{p.largest_asset || "None"}</span> <span className="text-[11px] text-[#8892A8]">({p.margin_ratio_pct >= 70 ? "Low risk" : "High concentration"})</span></div> },
                  { key: "health", header: "Health Score", align: "right", render: (p) => <span className={`font-bold font-mono ${p.margin_ratio_pct < 30 ? "text-[#EF4444]" : p.margin_ratio_pct < 70 ? "text-[#F59E0B]" : "text-[#10B981]"}`}>{p.margin_ratio_pct}%</span> },
                  { key: "status", header: "Status", render: (p) => <StatusPill value={p.status} /> }
                ]}
                rows={positionRows}
                rowKey={(p) => p.user_id}
              />
              <AdminPager page={posPager.page} total={posPager.total} pageSize={posPager.pageSize} onPage={posPager.setPage} testId="risk-margin-pos-pager" />
            </Panel>
          </div>
        )}

        {tab === "systemic" && (
          <div className="space-y-5">
            <div className="grid grid-cols-3 gap-4">
              <KpiCard label="Total System Exposure" value={fmt(totalExposure)} variant="yellow" />
              <KpiCard label="Critical Concentration" value={`${critical} Users`} variant="red" />
              <KpiCard label="System Health" value={`${avgMargin}/100`} variant="green" />
            </div>
            
            <Panel>
              <div className="p-4 border-b border-[#1E2845]"><h3 className="text-[14px] font-bold">Correlated Asset Risk</h3></div>
              <DataTable
                columns={[
                  { key: "asset_pair", header: "Asset Pair", render: (c) => <span className="font-bold">{c.asset_pair}</span> },
                  { key: "corr", header: "Correlation", align: "right", render: (c) => <span className="tabular-nums">{c.correlation_pct}%</span> },
                  { key: "users", header: "Exposed Users", align: "right", render: (c) => c.exposed_users },
                  { key: "exp", header: "Total Exposure", align: "right", render: (c) => fmt(c.total_exposure_inr) },
                  { key: "risk", header: "Risk Level", render: (c) => <StatusPill value={c.risk_level} /> },
                ]}
                rows={correlated}
                rowKey={(c) => c.asset_pair}
              />
              <AdminPager page={corrPager.page} total={corrPager.total} pageSize={corrPager.pageSize} onPage={corrPager.setPage} testId="risk-corr-pager" />
            </Panel>
          </div>
        )}

        {tab === "killswitch" && (
          <div className="grid grid-cols-2 gap-4">
            {[
              ["new_orders_disabled", "Halt all trading", "Stops new spot orders and halts active pairs", "danger"],
              ["withdrawals_halted", "Halt withdrawals", "Disables crypto + INR withdrawals globally", "yellow"],
              ["deposits_halted", "Halt deposits", "Disables crypto deposits and whitelist adds", "yellow"],
              ["otc_paused", "Pause OTC desk", "Legacy flag — use Resume if confirm fails with 503; does not block RM confirm on latest API", "ghost"],
            ].map(([key, title, sub, kind]) => {
              const active = Boolean(killState[key]);
              return (
              <Panel key={key} className="p-5 flex items-center justify-between">
                <div>
                  <div className="font-bold flex items-center gap-2">
                    {title}
                    {active ? <StatusPill value="critical" /> : <StatusPill value="healthy" />}
                  </div>
                  <div className="text-[11px] text-[#8892A8] mt-1">{sub}</div>
                </div>
                <Btn
                  kind={active ? "ghost" : kind === "danger" ? "danger" : "ghost"}
                  disabled={killBusy === key}
                  onClick={() => setKill(key, !active)}
                >
                  <Power size={13} /> {active ? "Resume" : "Activate"}
                </Btn>
              </Panel>
            );})}
          </div>
        )}
      </div>
    </AdminShell>
  );
}
